import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import MentionEditor from '../TextEditor/MentionEditor';
import useAwaitExternalEventStore, { getAwaitExternalEventStore,
  MODE_WEBHOOK,
  MODE_POLLING,
  WEBHOOK_MODE_STATIC,
  WEBHOOK_MODE_DYNAMIC,
  HTTP_METHOD_GET,
  HTTP_METHOD_POST,
  HTTP_METHOD_PUT,
  HTTP_METHOD_DELETE,
  INTERVAL_TYPE_MINUTES,
  INTERVAL_TYPE_HOURS,
  INTERVAL_TYPE_DAYS,
  RESPONSE_FORMAT_JSON,
  RESPONSE_FORMAT_XML,
  RESPONSE_FORMAT_TEXT,
  TIMEOUT_ERROR,
  TIMEOUT_PATH,
  CONDITION_TYPES,
  CONDITION_TYPE_FIELD_PATH,
  CONDITION_TYPE_JSONPATH,
  CONDITION_TYPE_REGEX,
  CONDITION_TYPE_TEXT_SEARCH,
  CONDITION_OPERATORS,
} from './useAwaitExternalEventStore';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import config from '../../config';
import { getCurrentTime } from '../../utils/getCurrentTime';
import checkKey from '../../utils/CheckKey';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import CustomPaper from '../common/CustomPaper';
import './AwaitExternalEventStyle.css';

const HTTP_METHODS = [
  { value: HTTP_METHOD_GET, label: 'GET' },
  { value: HTTP_METHOD_POST, label: 'POST' },
  { value: HTTP_METHOD_PUT, label: 'PUT' },
  { value: HTTP_METHOD_DELETE, label: 'DELETE' },
];

const INTERVAL_TYPES = [
  { value: INTERVAL_TYPE_MINUTES, label: 'Minutes' },
  { value: INTERVAL_TYPE_HOURS, label: 'Hours' },
  { value: INTERVAL_TYPE_DAYS, label: 'Days' },
];

const RESPONSE_FORMATS = [
  { value: RESPONSE_FORMAT_JSON, label: 'JSON' },
  { value: RESPONSE_FORMAT_XML, label: 'XML' },
  { value: RESPONSE_FORMAT_TEXT, label: 'Plain Text' },
];

const TIMEOUT_BEHAVIORS = [
  { value: TIMEOUT_ERROR, label: 'Error - Fail the workflow' },
  { value: TIMEOUT_PATH, label: 'Continue - Complete with timeout flag' },
];

const AwaitExternalEventTemplate = (props) => {
  const [loading, setLoading] = useState(true);

  const store = useMemo(() => getAwaitExternalEventStore(props.component_id), [props.component_id]);

  const [checkKeyState, setCheckKeyState] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);

  const data = store((s) => s.data);

  const fetchSessions = useCallback(async () => {
    if (!props.component_id) return;
    setSessionsLoading(true);
    try {
      const response = await send_request(
        `component-service/await_external_event/sessions/${props.component_id}`,
        '',
        {}
      );
      if (response?.data) {
        setSessions(response.data.sessions || []);
      }
    } catch (_error) {
      // Sessions not available
    } finally {
      setSessionsLoading(false);
    }
  }, [props.component_id]);

  useEffect(() => {
    const loadComponent = async () => {
      if (props.component_id === undefined) return;

      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        setLoading(false);
        props.setChildTemplateLoaded(true);
        fetchSessions();
        return;
      }

      setLoading(true);

      try {
        const componentResponse = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );

        if (componentResponse?.data) {
          const component = componentResponse.data.components?.[0] || componentResponse.data;
          store.getState().setComponentData(component);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(component.description);
          props.onLastModifiedChanged(store.getState().lastModified);
        }

        fetchSessions();
      } catch (error) {
        console.error('Error loading component data:', error);
      } finally {
        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };

    loadComponent();
  }, []);

  const handleKeyChange = (e) => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setOutputPlaceholderKey(placeholderValue);
    const storeData = JSON.parse(JSON.stringify(store.getState().data));
    const value = checkKey(placeholderValue, storeData);
    setCheckKeyState(value[0]);
    setErrorMessage(value[1]);
  };

  const handleEventSourceModeChange = (e) => {
    store.getState().setEventSourceMode(e.target.value);
  };

  const handleWebhookModeChange = (e) => {
    store.getState().setWebhookMode(e.target.value);
  };

  const handleWebhookCorrelationFieldChange = (e) => {
    store.getState().setWebhookCorrelationField(e.target.value);
  };

  const handleWebhookCorrelationValueChange = (value) => {
    store.getState().setWebhookCorrelationValue(value);
  };

  const getWebhookBaseUrl = () => {
    const webhookBase = config.WEBHOOK_URL || config.WEBHOOK_ENDPOINT?.PRODUCTION;
    if (webhookBase) return webhookBase.replace(/\/$/, '');
    const base = config.API_ADDRESS?.replace(/\/$/, '') || '';
    return `${base}/component-service`;
  };

  const getWebhookPathPrefix = () => {
    const base = getWebhookBaseUrl();
    return base.endsWith('/webhook') ? '/await_external_event' : '/webhook/await_external_event';
  };

  const buildWebhookUrl = (token, componentId = null) => {
    const base = getWebhookBaseUrl();
    const prefix = getWebhookPathPrefix();
    if (componentId) return `${base}${prefix}/${componentId}/${token}`;
    return `${base}${prefix}/${token}`;
  };

  const getStaticWebhookUrl = () => {
    if (data.webhookToken) return buildWebhookUrl(data.webhookToken, props.component_id);
    return null;
  };

  const handlePollingEndpointUrlChange = (value) => {
    store.getState().setPollingEndpointUrl(value);
  };

  const handlePollingHttpMethodChange = (e) => {
    store.getState().setPollingHttpMethod(e.target.value);
  };

  const handlePollingRequestBodyChange = (value) => {
    store.getState().setPollingRequestBody(value);
  };

  const handlePollingResponseFormatChange = (e) => {
    store.getState().setPollingResponseFormat(e.target.value);
  };

  const handlePollingIntervalValueChange = (e) => {
    const value = parseInt(e.target.value, 10) || 1;
    store.getState().setPollingIntervalValue(Math.max(1, value));
  };

  const handlePollingIntervalTypeChange = (e) => {
    store.getState().setPollingIntervalType(e.target.value);
  };

  const handleAddPollingHeader = () => {
    store.getState().addPollingHeader();
  };

  const handleUpdatePollingHeader = (index, field, value) => {
    store.getState().updatePollingHeader(index, field, value);
  };

  const handleRemovePollingHeader = (index) => {
    store.getState().removePollingHeader(index);
  };

  const handleAddPollingQueryParam = () => {
    store.getState().addPollingQueryParam();
  };

  const handleUpdatePollingQueryParam = (index, field, value) => {
    store.getState().updatePollingQueryParam(index, field, value);
  };

  const handleRemovePollingQueryParam = (index) => {
    store.getState().removePollingQueryParam(index);
  };

  const handleConditionTypeChange = (e) => {
    store.getState().setConditionType(e.target.value);
  };

  const handleConditionExpressionChange = (value) => {
    store.getState().setConditionExpression(value);
  };

  const handleConditionOperatorChange = (e) => {
    store.getState().setConditionOperator(e.target.value);
  };

  const handleConditionExpectedValueChange = (value) => {
    store.getState().setConditionExpectedValue(value);
  };

  const handleTimeoutEnabledChange = (e) => {
    store.getState().setTimeoutEnabled(e.target.checked);
  };

  const handleTimeoutValueChange = (e) => {
    const value = parseInt(e.target.value, 10) || 1;
    store.getState().setTimeoutValue(Math.max(1, value));
  };

  const handleTimeoutTypeChange = (e) => {
    store.getState().setTimeoutType(e.target.value);
  };

  const handleTimeoutBehaviorChange = (e) => {
    store.getState().setTimeoutBehavior(e.target.value);
  };

  const copyWebhookUrl = (fullUrl) => {
    navigator.clipboard.writeText(fullUrl);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const getActiveSession = () => sessions.find((s) => s.active);

  const eventSourceMode = data.eventSourceMode || MODE_WEBHOOK;
  const webhookMode = data.webhookMode || WEBHOOK_MODE_STATIC;
  const pollingHttpMethod = data.pollingHttpMethod || HTTP_METHOD_GET;
  const showPollingRequestBody =
    pollingHttpMethod === HTTP_METHOD_POST || pollingHttpMethod === HTTP_METHOD_PUT;
  const condition = data.condition || {};
  const conditionType = condition.type || CONDITION_TYPE_FIELD_PATH;
  const timeout = data.timeout || {};
  const activeSession = getActiveSession();
  const staticWebhookUrl = getStaticWebhookUrl();
  const selectedOperator = CONDITION_OPERATORS.find((op) => op.value === condition.operator);
  const operatorNeedsValue = selectedOperator ? selectedOperator.needsValue !== false : true;

  if (loading) {
    return <IndividualComponentLoader />;
  }

  return (
    <CustomPaper className="await-external-event-container">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className="await-external-event-banner">
            <Typography variant="body2">
              This component pauses the workflow and waits for an external event (via webhook or
              API polling) that matches a condition before continuing.
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12} className="await-external-event-section">
          <Typography className="await-external-event-label">Event Source</Typography>
          <Typography className="await-external-event-helper-text">
            How should this component receive external events?
          </Typography>
          <RadioGroup
            row
            value={eventSourceMode}
            onChange={handleEventSourceModeChange}
            className="await-external-event-radio-group"
          >
            <FormControlLabel
              value={MODE_WEBHOOK}
              control={<Radio />}
              label={
                <div className="await-external-event-radio-label">
                  <span className="radio-title">Webhook</span>
                  <span className="radio-description">
                    External systems POST data to a unique endpoint
                  </span>
                </div>
              }
            />
            <FormControlLabel
              value={MODE_POLLING}
              control={<Radio />}
              label={
                <div className="await-external-event-radio-label">
                  <span className="radio-title">API Polling</span>
                  <span className="radio-description">
                    Poll an external API at regular intervals
                  </span>
                </div>
              }
            />
          </RadioGroup>
        </Grid>

        {eventSourceMode === MODE_WEBHOOK && (
          <>
            <Grid item xs={12} className="await-external-event-section">
              <Typography className="await-external-event-label">Webhook Mode</Typography>
              <Typography className="await-external-event-helper-text">
                Choose how the webhook URL should be generated.
              </Typography>
              <RadioGroup
                row
                value={webhookMode}
                onChange={handleWebhookModeChange}
                className="await-external-event-radio-group"
              >
                <FormControlLabel
                  value={WEBHOOK_MODE_STATIC}
                  control={<Radio />}
                  label={
                    <div className="await-external-event-radio-label">
                      <span className="radio-title">Static URL</span>
                      <span className="radio-description">
                        Fixed URL you can pre-configure in external systems
                      </span>
                    </div>
                  }
                />
                <FormControlLabel
                  value={WEBHOOK_MODE_DYNAMIC}
                  control={<Radio />}
                  label={
                    <div className="await-external-event-radio-label">
                      <span className="radio-title">Dynamic URL</span>
                      <span className="radio-description">
                        Unique URL per workflow session (workflow passes URL to external system)
                      </span>
                    </div>
                  }
                />
              </RadioGroup>
            </Grid>

            {webhookMode === WEBHOOK_MODE_STATIC && (
              <Grid item xs={12} className="await-external-event-section">
                <Typography className="await-external-event-label">Static Webhook URL</Typography>
                <Typography className="await-external-event-helper-text">
                  Use this URL in your external system. The URL is stable and won't change between
                  workflow runs.
                </Typography>

                {staticWebhookUrl ? (
                  <div className="await-external-event-webhook-info static">
                    <div className="webhook-url-container">
                      <Typography variant="body2" className="webhook-url-label">
                        Webhook URL (save the workflow to generate):
                      </Typography>
                      <div className="webhook-url-value">
                        <code>{staticWebhookUrl}</code>
                        <Tooltip title={copiedToken ? 'Copied!' : 'Copy URL'}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              copyWebhookUrl(buildWebhookUrl(data.webhookToken, props.component_id))
                            }
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="await-external-event-info-box">
                    <HelpOutlineIcon className="info-icon" />
                    <Typography variant="body2">
                      Save the workflow to generate your static webhook URL. Once generated, you
                      can use this URL to configure your external system.
                    </Typography>
                  </div>
                )}

                <div className="await-external-event-subsection">
                  <Typography className="await-external-event-sublabel">
                    Correlation (Optional)
                  </Typography>
                  <Typography className="await-external-event-helper-text">
                    If multiple workflows may be waiting simultaneously, match a field in the
                    webhook payload to a value from your workflow to identify the correct session.
                  </Typography>
                  <div className="await-external-event-correlation-row">
                    <div className="await-external-event-correlation-field-wrapper">
                      <Typography variant="caption" className="await-external-event-field-label">
                        Field in Webhook Payload
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        placeholder="e.g., order_id"
                        value={data.webhookCorrelationField || ''}
                        onChange={handleWebhookCorrelationFieldChange}
                        fullWidth
                      />
                    </div>
                    <div className="await-external-event-correlation-value-wrapper">
                      <Typography variant="caption" className="await-external-event-field-label">
                        Expected Value (use placeholders)
                      </Typography>
                      <div className="await-external-event-mention-wrapper">
                        <MentionEditor
                          availablePlaceholders={props.availablePlaceholders || []}
                          text={data.webhookCorrelationValue || ''}
                          placeholder="e.g., ${order_id}"
                          onChange={handleWebhookCorrelationValueChange}
                          onFocus={() => {}}
                          error={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
            )}

            {webhookMode === WEBHOOK_MODE_DYNAMIC && (
              <Grid item xs={12} className="await-external-event-section">
                <Typography className="await-external-event-label">
                  Dynamic Webhook Configuration
                </Typography>
                <Typography className="await-external-event-helper-text">
                  Each workflow session will get a unique webhook URL. Your workflow should send
                  this URL to the external system (e.g., via API call or email) when it reaches
                  this component.
                </Typography>

                {activeSession && activeSession.webhookToken && (
                  <div className="await-external-event-webhook-info">
                    <div className="webhook-url-container">
                      <Typography variant="body2" className="webhook-url-label">
                        Current Session Webhook URL:
                      </Typography>
                      <div className="webhook-url-value">
                        <code>{buildWebhookUrl(activeSession.webhookToken)}</code>
                        <Tooltip title={copiedToken ? 'Copied!' : 'Copy URL'}>
                          <IconButton
                            size="small"
                            onClick={() => copyWebhookUrl(buildWebhookUrl(activeSession.webhookToken))}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}

                <div className="await-external-event-info-box">
                  <HelpOutlineIcon className="info-icon" />
                  <Typography variant="body2">
                    The webhook URL is available as a placeholder when this component executes.
                    Use it in subsequent components (like API Request or Email) to send the
                    callback URL to your external system.
                  </Typography>
                </div>
              </Grid>
            )}
          </>
        )}

        {eventSourceMode === MODE_POLLING && (
          <>
            <Grid item xs={12} className="await-external-event-section">
              <Typography className="await-external-event-label">API Endpoint URL</Typography>
              <Typography className="await-external-event-helper-text">
                Enter the URL to poll. Type "$" to insert placeholders or secrets.
              </Typography>
              <MentionEditor
                availablePlaceholders={props.availablePlaceholders || []}
                text={data.pollingEndpointUrl || ''}
                placeholder="https://api.example.com/status"
                onChange={handlePollingEndpointUrlChange}
                onFocus={() => {}}
                error={false}
                className="await-external-event-mention-editor"
              />
            </Grid>

            <Grid item xs={12} md={6} className="await-external-event-section">
              <Typography className="await-external-event-label">HTTP Method</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={pollingHttpMethod}
                  onChange={handlePollingHttpMethodChange}
                  className="await-external-event-select"
                >
                  {HTTP_METHODS.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} className="await-external-event-section">
              <Typography className="await-external-event-label">Response Format</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={data.pollingResponseFormat || RESPONSE_FORMAT_JSON}
                  onChange={handlePollingResponseFormatChange}
                  className="await-external-event-select"
                >
                  {RESPONSE_FORMATS.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} className="await-external-event-section">
              <Typography className="await-external-event-label">Polling Interval</Typography>
              <Typography className="await-external-event-helper-text">
                How often should the API be polled while waiting?
              </Typography>
              <div className="await-external-event-interval-row">
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  value={data.pollingInterval?.value || 5}
                  onChange={handlePollingIntervalValueChange}
                  inputProps={{ min: 1 }}
                  className="await-external-event-interval-value"
                />
                <FormControl size="small" className="await-external-event-interval-type">
                  <Select
                    value={data.pollingInterval?.type || INTERVAL_TYPE_MINUTES}
                    onChange={handlePollingIntervalTypeChange}
                  >
                    {INTERVAL_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Grid>

            <Grid item xs={12} className="await-external-event-section">
              <div className="await-external-event-section-header">
                <Typography className="await-external-event-label">HTTP Headers</Typography>
                <IconButton
                  size="small"
                  onClick={handleAddPollingHeader}
                  className="await-external-event-add-btn"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
              {(data.pollingHeaders || []).map((header, index) => (
                <div key={index} className="await-external-event-kv-row">
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Header name"
                    value={header.key || ''}
                    onChange={(e) => handleUpdatePollingHeader(index, 'key', e.target.value)}
                    className="await-external-event-kv-key"
                  />
                  <div className="await-external-event-kv-value">
                    <MentionEditor
                      availablePlaceholders={props.availablePlaceholders || []}
                      text={header.value || ''}
                      placeholder="Header value"
                      onChange={(value) => handleUpdatePollingHeader(index, 'value', value)}
                      onFocus={() => {}}
                      error={false}
                    />
                  </div>
                  <IconButton size="small" onClick={() => handleRemovePollingHeader(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </Grid>

            <Grid item xs={12} className="await-external-event-section">
              <div className="await-external-event-section-header">
                <Typography className="await-external-event-label">Query Parameters</Typography>
                <IconButton
                  size="small"
                  onClick={handleAddPollingQueryParam}
                  className="await-external-event-add-btn"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
              {(data.pollingQueryParams || []).map((param, index) => (
                <div key={index} className="await-external-event-kv-row">
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Parameter name"
                    value={param.key || ''}
                    onChange={(e) => handleUpdatePollingQueryParam(index, 'key', e.target.value)}
                    className="await-external-event-kv-key"
                  />
                  <div className="await-external-event-kv-value">
                    <MentionEditor
                      availablePlaceholders={props.availablePlaceholders || []}
                      text={param.value || ''}
                      placeholder="Parameter value"
                      onChange={(value) => handleUpdatePollingQueryParam(index, 'value', value)}
                      onFocus={() => {}}
                      error={false}
                    />
                  </div>
                  <IconButton size="small" onClick={() => handleRemovePollingQueryParam(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </Grid>

            {showPollingRequestBody && (
              <Grid item xs={12} className="await-external-event-section">
                <Typography className="await-external-event-label">Request Body</Typography>
                <div className="await-external-event-body-editor">
                  <MentionEditor
                    availablePlaceholders={props.availablePlaceholders || []}
                    text={data.pollingRequestBody || ''}
                    placeholder='{"key": "value"}'
                    onChange={handlePollingRequestBodyChange}
                    onFocus={() => {}}
                    error={false}
                    minHeight={150}
                  />
                </div>
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12} className="await-external-event-section">
          <Typography className="await-external-event-label">Resume Condition</Typography>
          <Typography className="await-external-event-helper-text">
            Define when the workflow should resume based on the received event data.
          </Typography>

          <div className="await-external-event-condition-type-selection">
            <Typography className="await-external-event-sublabel">Condition Type</Typography>
            <RadioGroup
              value={conditionType}
              onChange={handleConditionTypeChange}
              className="await-external-event-radio-group condition-type-radio-group"
            >
              {CONDITION_TYPES.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={<Radio />}
                  label={
                    <div className="await-external-event-radio-label">
                      <span className="radio-title">{type.label}</span>
                      <span className="radio-description">{type.description}</span>
                    </div>
                  }
                />
              ))}
            </RadioGroup>
          </div>

          {conditionType === CONDITION_TYPE_TEXT_SEARCH && (
            <div className="await-external-event-text-search-section">
              <Typography className="await-external-event-sublabel">Search Rules</Typography>
              <Typography className="await-external-event-helper-text small">
                Add conditions to match text anywhere in the received data.
              </Typography>

              {(condition.textSearchRules || []).map((rule, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <div className="await-external-event-text-search-connector">
                      <button
                        type="button"
                        className={`connector-btn ${
                          rule.group === (condition.textSearchRules[index - 1]?.group || 0) &&
                          rule.group !== 0
                            ? 'active'
                            : ''
                        }`}
                        onClick={() => {
                          const prevGroup = condition.textSearchRules[index - 1]?.group || 0;
                          const currentGroup = rule.group || 0;
                          if (currentGroup === prevGroup && currentGroup !== 0) {
                            store.getState().updateTextSearchRule(index, 'group', 0);
                          } else {
                            const newGroup =
                              prevGroup !== 0
                                ? prevGroup
                                : Math.max(...condition.textSearchRules.map((r) => r.group || 0)) + 1 || 1;
                            store.getState().updateTextSearchRule(index - 1, 'group', newGroup);
                            store.getState().updateTextSearchRule(index, 'group', newGroup);
                          }
                        }}
                      >
                        {rule.group === (condition.textSearchRules[index - 1]?.group || 0) &&
                        rule.group !== 0
                          ? 'OR'
                          : 'AND'}
                      </button>
                    </div>
                  )}
                  <div className="await-external-event-text-search-rule">
                    <div className="await-external-event-text-search-row-1">
                      <FormControl size="small" className="await-external-event-text-search-contains">
                        <Select
                          value={rule.contains !== false}
                          onChange={(e) =>
                            store.getState().updateTextSearchRule(index, 'contains', e.target.value)
                          }
                        >
                          <MenuItem value={true}>Contains</MenuItem>
                          <MenuItem value={false}>Does NOT contain</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rule.caseSensitive || false}
                            onChange={(e) =>
                              store.getState().updateTextSearchRule(index, 'caseSensitive', e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label="Case sensitive"
                        className="await-external-event-text-search-case"
                      />
                      <IconButton
                        size="small"
                        onClick={() => store.getState().removeTextSearchRule(index)}
                        className="await-external-event-delete-btn"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <div className="await-external-event-text-search-row-2">
                      <div className="await-external-event-mention-wrapper">
                        <MentionEditor
                          availablePlaceholders={props.availablePlaceholders || []}
                          text={rule.term || ''}
                          placeholder="Search text..."
                          onChange={(value) =>
                            store.getState().updateTextSearchRule(index, 'term', value)
                          }
                          onFocus={() => {}}
                          error={false}
                        />
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}

              <button
                type="button"
                className="await-external-event-add-btn"
                onClick={() => store.getState().addTextSearchRule()}
              >
                <AddIcon fontSize="small" />
                <span>Add Rule</span>
              </button>
            </div>
          )}

          {conditionType !== CONDITION_TYPE_TEXT_SEARCH && (
            <>
              <div className="await-external-event-condition-row">
                <div className="await-external-event-condition-field-wrapper">
                  <Typography variant="caption" className="await-external-event-field-label">
                    {conditionType === CONDITION_TYPE_JSONPATH
                      ? 'JSONPath Expression'
                      : conditionType === CONDITION_TYPE_REGEX
                      ? 'Regex Pattern'
                      : 'Field Path'}
                  </Typography>
                  <div className="await-external-event-mention-wrapper">
                    <MentionEditor
                      availablePlaceholders={props.availablePlaceholders || []}
                      text={condition.expression || condition.fieldPath || ''}
                      placeholder={
                        conditionType === CONDITION_TYPE_JSONPATH
                          ? '$.data.status'
                          : conditionType === CONDITION_TYPE_REGEX
                          ? '"status":\\s*"completed"'
                          : 'data.status'
                      }
                      onChange={handleConditionExpressionChange}
                      onFocus={() => {}}
                      error={false}
                    />
                  </div>
                </div>
                <div className="await-external-event-condition-operator-wrapper">
                  <Typography variant="caption" className="await-external-event-field-label">
                    Operator
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={condition.operator || 'equals'}
                      onChange={handleConditionOperatorChange}
                    >
                      {CONDITION_OPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {operatorNeedsValue && (
                  <div className="await-external-event-condition-value-wrapper">
                    <Typography variant="caption" className="await-external-event-field-label">
                      Expected Value
                    </Typography>
                    <div className="await-external-event-mention-wrapper">
                      <MentionEditor
                        availablePlaceholders={props.availablePlaceholders || []}
                        text={condition.expectedValue || ''}
                        placeholder={
                          condition.operator === 'in_list' || condition.operator === 'not_in_list'
                            ? 'value1, value2, value3'
                            : 'Expected value'
                        }
                        onChange={handleConditionExpectedValueChange}
                        onFocus={() => {}}
                        error={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </Grid>

        <Grid item xs={12} className="await-external-event-section">
          <FormControlLabel
            control={
              <Checkbox
                checked={data.timeoutEnabled || false}
                onChange={handleTimeoutEnabledChange}
                size="small"
              />
            }
            label={<span className="await-external-event-checkbox-label">Enable Timeout</span>}
            className="await-external-event-timeout-checkbox"
          />
          <Typography className="await-external-event-helper-text timeout-helper">
            Configure a maximum wait time. If the condition is not met before the timeout, the
            component will either fail or continue with a timeout flag.
          </Typography>

          {data.timeoutEnabled && (
            <div className="await-external-event-timeout-config">
              <div className="await-external-event-timeout-row">
                <Typography variant="body2">Wait for up to</Typography>
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  value={timeout.value || 1}
                  onChange={handleTimeoutValueChange}
                  inputProps={{ min: 1 }}
                  className="await-external-event-timeout-value"
                />
                <FormControl size="small" className="await-external-event-timeout-type">
                  <Select
                    value={timeout.type || INTERVAL_TYPE_HOURS}
                    onChange={handleTimeoutTypeChange}
                  >
                    {INTERVAL_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="await-external-event-timeout-behavior">
                <Typography variant="body2">On timeout:</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={timeout.behavior || TIMEOUT_ERROR}
                    onChange={handleTimeoutBehaviorChange}
                  >
                    {TIMEOUT_BEHAVIORS.map((behavior) => (
                      <MenuItem key={behavior.value} value={behavior.value}>
                        {behavior.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
        </Grid>

        <Grid item xs={12} className="await-external-event-section">
          <Typography className="await-external-event-label">Output Placeholder</Typography>
          <Typography className="await-external-event-helper-text">
            The event data that triggered the resume will be stored in this placeholder as JSON.
          </Typography>
          <W86PlaceholderTextField
            value={data.outputKey || ''}
            error={checkKeyState}
            helperText={checkKeyState ? errorMessage : null}
            placeholderText="Enter placeholder for event data"
            handleChange={handleKeyChange}
            customStyleProps={{ maxWidth: 400 }}
          />
        </Grid>

        <Grid item xs={12} className="await-external-event-section">
          <div className="await-external-event-section-header">
            <Typography className="await-external-event-label">Session Status</Typography>
            <IconButton
              size="small"
              onClick={fetchSessions}
              disabled={sessionsLoading}
              title="Refresh sessions"
            >
              {sessionsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </div>

          {sessions.length === 0 ? (
            <div className="await-external-event-session-empty">
              <Typography variant="body2">
                No active sessions. Sessions will appear here when the workflow is running and
                waiting for events.
              </Typography>
            </div>
          ) : (
            <div className="await-external-event-session-list">
              {sessions.slice(0, 5).map((session, index) => (
                <div
                  key={session.id}
                  className={`await-external-event-session-item ${
                    session.active ? 'active' : 'inactive'
                  }`}
                >
                  <div className="session-status-icon">
                    {session.active ? (
                      <CheckCircleIcon fontSize="small" className="active-icon" />
                    ) : (
                      <ErrorIcon fontSize="small" className="inactive-icon" />
                    )}
                  </div>
                  <div className="session-details">
                    <span className="session-id">
                      Session: {session.projectSessionId?.slice(0, 8)}...
                    </span>
                    <span className="session-mode">Mode: {session.eventSourceMode}</span>
                    <span className="session-status">
                      {session.active ? 'Waiting for event' : 'Completed'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Grid>
      </Grid>
    </CustomPaper>
  );
};

export default AwaitExternalEventTemplate;
